import {Button, type ButtonProps, Menu, type MenuProps} from "@mantine/core";
import { CaretDown as IconChevronDown } from "@phosphor-icons/react";
import {type CSSProperties, type ReactNode} from "react";

export interface SplitButtonOption {
  key?: string;
  label: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  leftSection?: ReactNode;
}

interface SplitButtonProps extends Omit<ButtonProps, "onClick"> {
  onClick: () => void;
  options?: SplitButtonOption[];
  menuWidth?: MenuProps["width"];
  menuPosition?: MenuProps["position"];
  menuShadow?: MenuProps["shadow"];
  menuTriggerLabel?: string;
}

export default function SplitButton({
                                      onClick,
                                      options = [],
                                      menuWidth = 180,
                                      menuPosition = "bottom-end",
                                      menuShadow = "md",
                                      menuTriggerLabel = "Show more actions",
                                      fullWidth,
                                      style,
                                      variant,
                                      color,
                                      size,
                                      radius,
                                      children,
                                      ...buttonProps
                                    }: SplitButtonProps) {
  if (options.length === 0) {
    return (
      <Button
        onClick={onClick}
        fullWidth={fullWidth}
        style={style}
        variant={variant}
        color={color}
        size={size}
        radius={radius}
        {...buttonProps}
      >
        {children}
      </Button>
    );
  }

  const groupStyle: CSSProperties = {
    ...(fullWidth ? {display: "flex", width: "100%"} : {}),
    ...(style as CSSProperties | undefined),
  };
  const mainButtonStyle: CSSProperties | undefined = fullWidth ? {flexGrow: 1} : undefined;

  return (
    <Menu width={menuWidth} position={menuPosition} shadow={menuShadow}>
      <Button.Group style={groupStyle}>
        <Button
          onClick={onClick}
          style={mainButtonStyle}
          variant={variant}
          color={color}
          size={size}
          radius={radius}
          {...buttonProps}
        >
          {children}
        </Button>
        <Menu.Target>
          <Button
            px={8}
            variant={variant}
            color={color}
            size={size}
            radius={radius}
            aria-label={menuTriggerLabel}
          >
            <IconChevronDown size={16}/>
          </Button>
        </Menu.Target>
      </Button.Group>

      <Menu.Dropdown>
        {options.map((option, index) => (
          <Menu.Item
            key={option.key ?? String(index)}
            leftSection={option.leftSection}
            disabled={option.disabled}
            onClick={option.onClick}
          >
            {option.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}
